import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { Cmd } from '@/components/wasm/lib';
import type { Layer } from '../layers';
import type { ShapeFileWasm } from './constants';
import { CURSOR_MODE } from './constants';
import { state } from './state';
import type { TechnicalIndicatorData } from '../layers/patterns/requierdInjection/types';

const ACTIVATE: any = true;
const DEACTIVATE: any = false;

export function isDateLoaded(date: Date): boolean {
    let result = false;
    state.loadedDate.forEach(loaded => {
        const from = new Date(loaded.getFullYear(), loaded.getMonth(), 1);
        const to = new Date(loaded.getFullYear(), loaded.getMonth() + 1, 0);
        if (from <= date && date <= to) {
            result = true;
        }
    });
    return result;
}

export function isProductionLoaded(date: Date): boolean {
    let result = false;
    state.productionLoadedDate.forEach(loaded => {
        const from = new Date(loaded.getFullYear(), loaded.getMonth(), 1);
        const to = new Date(loaded.getFullYear(), loaded.getMonth() + 1, 0);
        if (from <= date && date <= to) {
            result = true;
        }
    });
    return result;
}

export function hasNonZeroValues(data: TechnicalIndicatorData[]): boolean {
    for (const row of data)
        if (row.injection_value !== 0 || row.ineffect_value !== 0) {
            return true;
        }
    return false;
}

export function isExtendedProduction(): boolean {
    let result = false;
    state.layerStructure.forEach((layer, guid) => {
        if (layer.pattern.key === 'well_debit') {
            const settings = state.layerSettings.get(guid);
            if (settings !== undefined) {
                if (Number(settings.debitType) === 3) {
                    result = true;
                }
            }
        }
    });
    return result;
}

export async function setCurrentDate(currentDate: Date) {
    // if (state.selectedLayer.pattern.id === 'voronoi' && state.currentDate !== currentDate) {
    //     console.log(state.currentDate, currentDate);
    //     console.log('start calculating');
    //     state.isVoronoiReady = false;
    // }

    state.currentDate = currentDate;
    const date = dayjs(currentDate);

    const parsedCurrentDate: any = date.isValid() ? dayjs(currentDate).format('YYYY-MM-DDTHH:mm:ss') : '';
    await Cmd.exec('set_current_time', parsedCurrentDate); // '2006-08-28T00:00:00'
    //await delay(300);
    await Cmd.exec('update_plot');

    if (!isDateLoaded(currentDate)) {
        state.datesOnLoad = [...state.datesOnLoad, currentDate];
        state.dateToLoad = currentDate;
    }

    if (isExtendedProduction() && !isProductionLoaded(currentDate)) {
        state.productionDatesOnLoad = [...state.productionDatesOnLoad, currentDate];
        state.productionDateToLoad = currentDate;
    }
    state.activeStratumId = state.layerSettings.get(state.selectedLayer.guid)?.activeStratumId;
    const currentActiveFieldId = state.layerSettings.get(state.selectedLayer?.guid)?.activeFieldId;
    state.activeStratumKey = `${currentActiveFieldId}-${state.activeStratumId}`;
    // await delay(300);
    const rawSettings = state.layerSettings.get(state.selectedLayer.guid) ?? {};
    const updatedSettings = { ...rawSettings, _version: Date.now() };
    await setLayerSettings(state.selectedLayer, updatedSettings);
}

export const resetDrawMode = () => {
    state.drawMode = DEACTIVATE;
};

const dropPaintTools = async () => {
    await Cmd.exec('set_ruler_active', DEACTIVATE);
    await Cmd.exec('set_lasso_active', DEACTIVATE);
    await Cmd.exec('set_circle_active', DEACTIVATE);
    await Cmd.exec('set_chain_active', DEACTIVATE);
    await Cmd.exec('set_equidistant_active', DEACTIVATE);

    await setUserCircleLayerVisible(DEACTIVATE);
    await Cmd.exec('set_user_circles_active', DEACTIVATE);
    state.userCircleMode = DEACTIVATE;
};

export const setRulerActive = async () => {
    state.cursorMode = CURSOR_MODE.RULER;
    await dropPaintTools();
    resetDrawMode();
    await Cmd.exec('set_ruler_active', ACTIVATE);
};

export const setUserCircleMode = async (value: boolean) => {
    if (value) {
        resetDrawMode();
        state.cursorMode = CURSOR_MODE.USER_CIRCLE;
        await dropPaintTools();
        await Cmd.exec('set_user_circles_active', ACTIVATE);

        await setUserCircleLayerVisible(ACTIVATE);

        state.userCircleMode = ACTIVATE;
    } else {
        await clearPaintTools();
    }
};

export const toggleCreateWellMode = async () => {
    if (state.cursorMode !== CURSOR_MODE.NEW_WELL) {
        resetDrawMode();
        state.cursorMode = CURSOR_MODE.NEW_WELL;
        await dropPaintTools();
        await Cmd.exec('set_click_listener_active', ACTIVATE);
    } else {
        await clearPaintTools();
        await Cmd.exec('set_click_listener_active', DEACTIVATE);
    }
};

const setUserCircleLayerVisible = async (value: boolean) => {
    const layers = [...state.layerStructure.values()];
    const userCircleLayer = layers.find(l => l.pattern.id === 'UserCircle');
    if (userCircleLayer) await setLayerVisible(userCircleLayer, value);
};

export const setSelectedUserCircleX = async (value: string | number | null) => {
    if (state.selectedUserCircle && value !== null) {
        state.selectedUserCircle.x = Number(value);
        await updateUserCircle();
    }
};

export const setSelectedUserCircleY = async (value: string | number | null) => {
    if (state.selectedUserCircle && value !== null) {
        state.selectedUserCircle.y = Number(value);
        await updateUserCircle();
    }
};

export const setSelectedUserCircleRadius = async (value: string | number | null) => {
    if (state.selectedUserCircle && value !== null && Number(value) > 0) {
        state.selectedUserCircle.radius = Number(value);
        await updateUserCircle();
    }
};

const updateUserCircle = async () => {
    const { selectedUserCircle } = state;
    if (selectedUserCircle) {
        await Cmd.exec('set_user_circle_data', selectedUserCircle as any);
        await Cmd.exec('update_plot');
    }
};

export const updateUserCircleIntersectionTextVisible = async () => {
    const layers = [...state.layerStructure.values()];
    const userCircleLayer = layers.find(l => l.pattern.id === 'UserCircle');

    const { guid } = userCircleLayer ?? {};
    if (!isNil(guid) && userCircleLayer) {
        const settings = state.layerSettings.get(guid);
        if (settings) {
            await setLayerSettings(userCircleLayer, {
                ...settings,
                intersectionTextVisible: !settings.intersectionTextVisible,
            });
        }
    }
};

export const setLassoActive = async () => {
    state.cursorMode = CURSOR_MODE.LASSO;
    await dropPaintTools();
    await Cmd.exec('set_lasso_active', ACTIVATE);
};

export const setCircleActive = async () => {
    state.cursorMode = CURSOR_MODE.CIRCLE;
    await dropPaintTools();
    await Cmd.exec('set_circle_active', ACTIVATE);
};

export const setChainActive = async () => {
    state.cursorMode = CURSOR_MODE.CHAIN;
    await dropPaintTools();
    await Cmd.exec('set_chain_active', ACTIVATE);
};

export const setOvalActive = async () => {
    state.cursorMode = CURSOR_MODE.OVAL;
    await dropPaintTools();
    await Cmd.exec('set_equidistant_active', ACTIVATE);
};

export const clearPaintTools = async () => {
    await dropPaintTools();
    resetDrawMode();
    state.cursorMode = CURSOR_MODE.VIEWING;
};

export const setMapScale = async (newVal: number) => {
    state.scale = newVal;
    await Cmd.exec('set_scale', newVal as any);
    await Cmd.exec('update_plot');
};

export const fitPlot = async () => {
    await Cmd.exec('fit_plot');
    await Cmd.exec('update_plot');
};

export const exportData = async () => {
    await Cmd.exec('export_data');
    await Cmd.exec('update_plot');
};

export const importData = async () => {
    await Cmd.exec('import_data');
    await Cmd.exec('update_plot');
};

export const exportToSVG = async () => {
    await Cmd.exec('export_to_svg');
    await Cmd.exec('update_plot');
};

export const updateShapeSettings = async () => {
    const layers = [...state.layerStructure.values()];
    const layer = layers.find(l => l.pattern.id === 'Contour');
    const { guid } = layer ?? {};

    if (!isNil(guid) && layer) {
        const selectedShapesRaw: ShapeFileWasm[] = JSON.parse(
            await Cmd.get_v2({
                id: `${guid}-shapes`,
                type: 'layer_data',
                data_type: 'shape_files_list',
            }),
        );
        if (Array.isArray(selectedShapesRaw)) {
            // Построим карту: title (из API) -> serverId
            const serverByTitle = new Map<string, number>();
            try {
                (state.shapeFiles || []).forEach((sf: any) => {
                    const key = String(sf?.title || '')
                        .trim()
                        .toLowerCase();
                    if (key) serverByTitle.set(key, Number(sf.id));
                });
            } catch {}

            // Нормализуем объекты из wasm: сопоставим server_id по имени
            type ShapeSettings = {
                id: number;
                file_name: string;
                visible: boolean;
                server_id?: number;
                wasm_id?: number;
            };
            const normalized: ShapeSettings[] = [];
            const seenWasm = new Set<number>();
            for (const s of selectedShapesRaw) {
                const wasm_id = Number((s as any)?.id);
                if (seenWasm.has(wasm_id)) continue;
                seenWasm.add(wasm_id);
                const nameKey = String((s as any)?.file_name || '')
                    .trim()
                    .toLowerCase();
                const server_id = serverByTitle.get(nameKey);
                normalized.push({
                    id: Number(wasm_id),
                    file_name: String((s as any)?.file_name || ''),
                    visible: false,
                    server_id: server_id,
                    wasm_id: wasm_id,
                });
            }

            // Формируем новый список ТОЛЬКО из актуального ответа wasm,
            // но переносим флаг visible из предыдущих настроек, если совпал id
            const prevSettings = state.layerSettings.get(layer.guid) ?? {};
            const prevList = (((prevSettings as any).shape_files_settings as any[]) || []) as ShapeSettings[];
            const prevVisible = new Map<number, boolean>();
            for (const p of prevList) {
                prevVisible.set(Number(p?.id), Boolean((p as any)?.visible));
            }

            const uniqueFinal = Array.from(new Map(normalized.map(n => [Number(n.id), n])).values()).map(item => ({
                ...item,
                visible: prevVisible.get(Number(item.id)) ?? false,
            }));
            setLayerSettings(layer, { ...prevSettings, shape_files_settings: uniqueFinal });
        }
    }
};

export const deleteShapeFile = async (id: number) => {
    // id может быть серверным или wasm-id. Попробуем найти соответствие в настройках
    const layers = [...state.layerStructure.values()];
    const layer = layers.find(l => l.pattern.id === 'Contour');
    const layerGuid = layer?.guid;
    let wasmId = Number(id);
    if (layerGuid) {
        const settings = state.layerSettings.get(layerGuid) as any;
        const list = (settings?.shape_files_settings || []) as Array<{
            id: number;
            server_id?: number;
            wasm_id?: number;
            file_name: string;
        }>;
        const byServer = list.find(s => Number(s.server_id ?? s.id) === Number(id));
        if (byServer && byServer.wasm_id !== undefined) wasmId = Number(byServer.wasm_id);
        // Также сразу уберём запись из настроек
        const next = (list || []).filter(s => Number(s.server_id ?? s.id) !== Number(id));
        setLayerSettings(layer as any, { ...(settings || {}), shape_files_settings: next });
    }
    Cmd.exec('clear_shape_file', [wasmId] as any);
    await updateShapeSettings();
    // Обновим чекбокс в модалке
    if (layerGuid) {
        const current = (state?.shapeModalSelectionByLayer?.get(layerGuid) || {}) as Record<
            number,
            { checked: boolean; title: string }
        >;

        if (current[id]) {
            state.shapeModalSelectionByLayer.set(layerGuid, { ...current, [id]: { ...current[id], checked: false } });
        }
    }
};

export const clearShapesFromCanvasByIds = async (ids: number[]): Promise<void> => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    const layers = [...state.layerStructure.values()];
    const layer = layers.find(l => l.pattern.id === 'Contour');
    const layerGuid = layer?.guid;
    const settings = layerGuid ? (state.layerSettings.get(layerGuid) as any) : undefined;
    const list = (settings?.shape_files_settings || []) as Array<{ id: number; server_id?: number; wasm_id?: number }>;
    for (const id of ids) {
        const item = list.find(s => Number(s.server_id ?? s.id) === Number(id));
        const wasmId = item?.wasm_id ?? item?.id ?? id;
        await Cmd.exec('clear_shape_file', [Number(wasmId)] as any);
    }
    await updateShapeSettings();
};

export const createLayer = async (layer: Layer) => {
    state.layerStructure.set(layer.guid, layer);
    if (layer.pattern.key !== undefined && !layer.pattern.pinned) {
        await Cmd.exec('create_layer', { id: layer.guid, type: layer.pattern.key } as any);
        if (layer.pattern.key === 'contour') {
            await Cmd.exec('create_layer', { id: `${layer.guid}-shapes`, type: 'shape_contour' } as any);
        }
        await Cmd.exec('update_plot');
    }
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const setLayerVisible = async (layer: Layer, visible: boolean, touchEye = true) => {
    if (touchEye) {
        state.layerVisibility.set(layer.guid, visible);
    }
    if (layer.pattern.key !== undefined && !layer.pattern.pinned) {
        await Cmd.exec('set_layer_visible', { id: layer.guid, visible: visible } as any);
        if (layer.pattern.key === 'contour') {
            await Cmd.exec('set_layer_visible', { id: `${layer.guid}-shapes`, visible } as any);
        }
        state.activeStratumId = state.layerSettings.get(layer.guid)?.activeStratumId;
        const currentActiveFieldId = state.layerSettings.get(state.selectedLayer?.guid)?.activeFieldId;
        state.activeStratumKey = `${currentActiveFieldId}-${state.activeStratumId}`;
        await Cmd.exec('update_plot');
        await delay(300);
        const requiredInjectionKey = `${dayjs(state.currentDate).format('MM.YYYY')}-${state.layerSettings.get(layer.guid)?.activeFieldId}-${state.activeStratumId}`;
        await setCurrentRequiredInjection(layer, requiredInjectionKey);

        if (visible) {
            const orderedLayers = Array.from(state.layerStructure.values()).filter(l => !l?.pattern?.hide);
            const index = orderedLayers.findIndex(l => l.guid === layer.guid);

            if (index !== -1) {
                // Если это первый слой
                if (index === 0) {
                    setLayerZ(layer, 999);
                } else if (index === 1) {
                    // Если второй слой
                    setLayerZ(layer, 899);
                } else {
                    // Остальные слои
                    setLayerZ(layer, 999 - index * 100);
                }
            }
        }
    }
};

export const setLayerSettings = async (layer: Layer, settings: Record<string, unknown>) => {
    state.layerSettings.set(layer.guid, settings);
    state.activeStratumId = state.layerSettings.get(layer.guid).activeStratumId;
    const currentActiveFieldId = state.layerSettings.get(layer?.guid)?.activeFieldId;
    state.activeStratumKey = `${currentActiveFieldId}-${state.activeStratumId}`;

    if (layer.pattern.key === 'voronoi') {
        state.isVoronoiReady = false;
    }

    if (layer.pattern.key !== undefined) {
        await Cmd.exec('set_layer_settings', { id: layer.guid, settings: settings } as any);
    }
    if (layer.pattern.key === 'contour') {
        await Cmd.exec('set_layer_settings', {
            id: `${layer.guid}-shapes`,
            settings: settings,
        } as any);
    }
    await Cmd.exec('update_plot');

    if (layer.pattern.key === 'well_debit') {
        if (Number(settings.debitType) === 3) {
            if (!isProductionLoaded(state.currentDate)) {
                state.productionDatesOnLoad = [...state.productionDatesOnLoad, state.currentDate];
                state.productionDateToLoad = state.currentDate;
            }
        }
    }
    // if (layer.pattern.key === 'voronoi') {
    //     await setCurrentRequiredInjection(layer, settings);
    // }
};

export const setCurrentRequiredInjection = async (layer: Layer, requiredInjectionKey: string) => {
    try {
        const reqInj = await Cmd.get_v2({
            type: 'layer_data',
            id: layer.guid,
            data_type: 'voronoi_settings_data',
        });
        // const requiredInjectionKey = `${dayjs(state.currentDate).format('MM.YYYY')}-${state.activeStratumId}`;
        if (reqInj && typeof reqInj === 'string') {
            // Check if response is valid JSON
            if (reqInj.trim().startsWith('{') || reqInj.trim().startsWith('[')) {
                try {
                    const parsed = JSON.parse(reqInj);
                    console.log('parsed', parsed);
                    console.log(`Successfully got ${parsed.length} injection records - key ${requiredInjectionKey}`);
                    if (hasNonZeroValues(parsed)) state.requiredInjection[requiredInjectionKey] = parsed;
                } catch (parseError) {
                    console.error('JSON parse error:', parseError);
                    state.requiredInjection[requiredInjectionKey] = [];
                }
            } else {
                console.error('Invalid response format:', reqInj.substring(0, 100));
                state.requiredInjection[requiredInjectionKey] = [];
            }
        } else {
            state.requiredInjection[requiredInjectionKey] = [];
        }
    } catch (error) {
        console.error('Error getting required injection:', error);
        state.requiredInjection[requiredInjectionKey] = [];
    }
};

export const removeLayer = async (layer: Layer) => {
    console.log(`REMOVE LAYER ${layer.guid}`);
    state.layerStructure.delete(layer.guid);
    state.layerVisibility.delete(layer.guid);
    state.layerSettings.delete(layer.guid);

    if (!layer.pattern.pinned) {
        await Cmd.exec('remove_layer', { id: layer.guid } as any);
        if (layer.pattern.key === 'contour') {
            await Cmd.exec('remove_layer', { id: `${layer.guid}-shapes` } as any);
        }
        await Cmd.exec('update_plot');
    }
};

export const searchWell = (fields: Array<{ id: number; label: string }>, wellId: number) => {
    fields.forEach(async ({ id: fieldId }) => {
        const param: unknown = {
            fieldId: fieldId,
            wellId: wellId,
        };
        await Cmd.exec('set_focused_well', param);
        await Cmd.exec('update_plot');
    });
};

export const deselectWell = async () => {
    const param: any = {
        fieldId: -1,
        wellId: -1,
    };
    await Cmd.exec('set_focused_well', param);
    await Cmd.exec('update_plot');
};

export const drawMode = async (newVal: boolean) => {
    if (newVal) {
        await dropPaintTools();
        state.cursorMode = CURSOR_MODE.VIEWING;
    } else {
        await clearPaintTools();
    }
    state.drawMode = newVal;
};
// ... existing code ...

export const onCheckSelectedWell = (wellId: number, checked: boolean) => {
    state.checkedSelectedWells[wellId] = checked;
};

export const setLayerZ = async (layer: Layer, z: number): void => {
    if (layer.pattern.key !== undefined && !layer.pattern.pinned) {
        await Cmd.exec('set_layer_z', { id: layer.guid, z } as any);
        await Cmd.exec('update_plot');
    }
};

export const reorderLayers = async (newLayerOrder: Layer[]): void => {
    state.layerStructure.clear();

    newLayerOrder.forEach(layer => {
        state.layerStructure.set(layer.guid, layer);
    });

    // Затем обновляем Z-индексы для слоев на канвасе
    const managedLayers = newLayerOrder.filter(layer => layer.pattern.key !== undefined && !layer.pattern.pinned);

    for (let i = 0; i < managedLayers.length; i++) {
        if (i === 0) {
            setLayerZ(managedLayers[i], 999);
        } else {
            setLayerZ(managedLayers[i], managedLayers.length - i);
        }
    }

    await Cmd.exec('update_plot');
};
export const selectLayer = (layer: Layer) => {
    state.selectedLayer = layer;
};

export const getContourLayer = (): Layer | undefined => {
    const layers = [...state.layerStructure.values()];
    return layers.find(l => l.pattern.id === 'Contour');
};

export const getLoadedShapeIds = (layerGuid?: string): number[] => {
    if (!layerGuid) return [];
    const settings = state.layerSettings.get(layerGuid) as any;
    const shapeFilesSettings = settings?.shape_files_settings as
        | Array<{ id: number; server_id?: number; file_name?: string }>
        | undefined;
    return Array.isArray(shapeFilesSettings) ? shapeFilesSettings.map(s => Number((s as any).server_id ?? s.id)) : [];
};

export const initializeSelectedLayer = () => {
    const layers = [...state.layerStructure.values()];
    if (layers.length > 0 && !state.selectedLayer) {
        state.selectedLayer = layers[0];
    }
};
